"use client";

import React from "react";
import { Loader, Check, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function JoinTheSmokedClub() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="הצטרפו-למועדון-המעושן" ref={ref} className={`dark bg-background text-foreground py-20 px-4 transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className="max-w-2xl mx-auto">
        <div className="bg-secondary border border-primary rounded-xl p-8 md:p-10 shadow-2xl relative overflow-hidden">
          {}
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
          
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-2 text-primary">
              הצטרפו למועדון המעושן
            </h2>
            <p className="text-center text-muted-foreground mb-8">
              הירשמו לקבלת הטבות בלעדיות, עדכונים על נתחים חדשים ומבצעים חמים.
            </p>

            <FormProvider formId="club_signup_home" sectionName="join-club-section" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            favorite_meat: "",
            marketing_consent: false
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            marketing_consent: {
              required: true
            }
          }} submitText="הצטרפו עכשיו" successMessage="תודה שהצטרפתם למועדון המעושן! הטבה ראשונה בדרך אליכם." errorMessage="אירעה שגיאה בהרשמה. אנא נסו שנית." className="w-full">
              <ClubSignupForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ClubSignupForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 bg-background/50 border border-primary/20 rounded-lg text-center animate-in fade-in zoom-in duration-300">
        <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mb-4">
          <Check className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">ברוכים הבאים!</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary/50 hover:bg-primary/10">
          הרשמה נוספת
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="full_name" rules={{
      required: "שדה חובה"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>שם מלא</FormLabel>
            <FormControl>
              <Input placeholder="יוסי כהן" {...field} className="bg-background/50 border-input/50 focus:border-primary" />
            </FormControl>
            <FormDescription className="text-xs">השם כפי שיופיע במועדון</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "שדה חובה",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "כתובת אימייל לא תקינה"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>כתובת אימייל</FormLabel>
            <FormControl>
              <Input type="email" placeholder="example@domain.com" {...field} className="bg-background/50 border-input/50 focus:border-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>מספר טלפון</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="05-1234567" {...field} className="bg-background/50 border-input/50 focus:border-primary" />
              </FormControl>
              <FormDescription className="text-xs">נדרש לקבלת מבצעים מיוחדים</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="favorite_meat" render={({
        field
      }) => <FormItem>
              <FormLabel>נתח בשר מועדף</FormLabel>
              <FormControl>
                <select {...field} className="flex h-9 w-full rounded-md border border-input/50 bg-background/50 px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 focus:border-primary text-foreground">
                  <option value="" disabled>בחר סוג בשר</option>
                  <option value="אנטריקוט">אנטריקוט</option>
                  <option value="סינטה">סינטה</option>
                  <option value="פרגיות">פרגיות</option>
                  <option value="בקר מעושן">בקר מעושן</option>
                  <option value="קבב">קבב</option>
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="marketing_consent" rules={{
      required: "יש לאשר את תנאי ההצטרפות"
    }} render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-x-reverse space-y-0 rounded-md border border-input/30 p-4 bg-background/30">
            <FormControl>
              <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} className="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground accent-primary mt-1" {...field} />
            </FormControl>
            <div className="space-y-1 leading-none mr-3">
              <FormLabel className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                אני מסכים לקבל עדכונים והצעות מיוחדות
              </FormLabel>
              <FormDescription className="text-xs">
                הצטרפות למועדון כוללת קבלת ניוזלטר והצעות בלעדיות
              </FormDescription>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md flex items-center gap-2 text-destructive text-sm">
          <CircleAlert className="w-4 h-4" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-6 text-lg shadow-lg shadow-primary/20">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            שולח...
          </> : "הצטרפו למועדון"}
      </Button>
    </Form>;
}